import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { Button, Textarea } from "@/components/ui/primitives";
import { springs } from "@/components/ui/motion";
import { cn } from "@/packages/utils";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";

/* ------------------------------------------------------------------ */
/* Schema                                                              */
/* ------------------------------------------------------------------ */

interface Category {
  _id: string;
  name: string;
}

interface ChecklistCreateDialogProps {
  open: boolean;
  onClose: () => void;
  auditId: Id<"audits">;
  categories: Category[];
  onCreated: (itemId: string) => void;
}

const createSchema = z.object({
  categoryId: z.string().min(1),
  description: z.string().min(1),
  status: z.enum(["MISSING", "UPLOADED", "APPROVED"]),
  required: z.boolean(),
  isAnnex: z.boolean(),
  notes: z.string().optional(),
  clauseRefs: z.array(z.string()).optional(),
  questions: z.array(z.unknown()).optional(),
});

type CreateFormValues = z.infer<typeof createSchema>;

/* ------------------------------------------------------------------ */
/* Status helpers                                                      */
/* ------------------------------------------------------------------ */

const STATUS_COLORS = {
  MISSING: "bg-amber-500",
  UPLOADED: "bg-blue-500",
  APPROVED: "bg-green-500",
} as const;

/* ------------------------------------------------------------------ */
/* Toggle Switch                                                       */
/* ------------------------------------------------------------------ */

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
        checked ? "bg-accent" : "bg-border",
      )}
    >
      <span
        className={cn(
          "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200",
          checked ? "translate-x-4" : "translate-x-0",
        )}
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Styled Select Dropdown                                              */
/* ------------------------------------------------------------------ */

function StyledSelect({
  value,
  options,
  onChange,
  hasColorDot,
}: {
  value: string;
  options: Array<{ value: string; label: string; color?: string }>;
  onChange: (val: string) => void;
  hasColorDot?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value) ?? options[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-muted/30 transition-colors"
      >
        {hasColorDot && current.color && (
          <span className={cn("h-2 w-2 rounded-full", current.color)} />
        )}
        <span className="flex-1 text-left">{current.label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence>
        {open && (
          <>
            {/* Invisible backdrop to close */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: presentation backdrop only needs click-to-close */}
            <div
              role="presentation"
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-border bg-card p-1 shadow-lg"
            >
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                    opt.value === value
                      ? "bg-accent/10 text-accent-foreground"
                      : "hover:bg-muted/50",
                  )}
                >
                  {hasColorDot && opt.color && (
                    <span className={cn("h-2 w-2 rounded-full", opt.color)} />
                  )}
                  {opt.label}
                  {opt.value === value && (
                    <Check className="ml-auto h-3.5 w-3.5" />
                  )}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Collapsible Section (matches edit panel)                            */
/* ------------------------------------------------------------------ */

function CollapsibleSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-muted/30 transition-colors rounded-lg"
      >
        <motion.span
          animate={{ rotate: open ? 0 : -90 }}
          transition={springs.snappy}
          className="shrink-0"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
        {title}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.smooth}
            style={{ overflow: "hidden" }}
          >
            <div className="px-4 pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

export function ChecklistCreateDialog({
  open,
  onClose,
  auditId,
  categories,
  onCreated,
}: ChecklistCreateDialogProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [generalOpen, setGeneralOpen] = useState(true);

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      categoryId: categories[0]?._id ?? "",
      description: "",
      status: "MISSING",
      required: false,
      isAnnex: false,
      notes: "",
      clauseRefs: [],
      questions: [],
    },
  });

  const mutation = useApiMutation(api.checklist_mutations.createChecklistItem, {
    name: "create-checklist-item",
    successMessage: t("checklist.edit.itemCreated"),
  });

  async function onSubmit(values: CreateFormValues) {
    const itemId = await mutation.mutateAsync({
      auditId,
      // Boundary cast: categoryId comes from the form as a plain string,
      // but the mutation requires a branded Convex Id<"checklistCategories">.
      // The value is always sourced from actual category._id strings fetched
      // from the backend, so this cast is safe at the domain boundary.
      categoryId: values.categoryId as Id<"checklistCategories">,
      description: values.description,
      required: values.required,
      isAnnex: values.isAnnex,
      status: values.status,
      notes: values.notes ?? undefined,
      clauseRefs: values.clauseRefs,
      questions: [],
    });

    form.reset();
    onCreated(itemId);
    onClose();
  }

  const categoryOptions = categories.map((cat) => ({
    value: cat._id,
    label: cat.name,
  }));

  const statusOptions = [
    {
      value: "MISSING",
      label: t("checklist.status.missing"),
      color: STATUS_COLORS.MISSING,
    },
    {
      value: "UPLOADED",
      label: t("checklist.status.uploaded"),
      color: STATUS_COLORS.UPLOADED,
    },
    {
      value: "APPROVED",
      label: t("checklist.status.approved"),
      color: STATUS_COLORS.APPROVED,
    },
  ];

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-surface p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.25)] max-w-lg w-full max-h-[80vh] overflow-y-auto">
          <Dialog.Title className="text-lg font-semibold mb-4">
            {t("checklist.edit.createTitle")}
          </Dialog.Title>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Category — styled dropdown */}
            <div className="space-y-1">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Custom dropdown component does not expose a native input */}
              <label className="text-sm font-medium">
                {t("checklist.edit.categoryLabel")}
              </label>
              <StyledSelect
                value={form.watch("categoryId")}
                options={categoryOptions}
                onChange={(val) =>
                  form.setValue("categoryId", val, { shouldDirty: true })
                }
              />
              {form.formState.errors.categoryId && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.categoryId.message}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label
                htmlFor="create-description"
                className="text-sm font-medium"
              >
                {t("checklist.edit.descriptionLabel")}
              </label>
              <Textarea
                id="create-description"
                {...form.register("description")}
                rows={3}
                className="w-full"
              />
              {form.formState.errors.description && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* Properties section */}
            <CollapsibleSection
              title={t("checklist.edit.generalSection")}
              open={generalOpen}
              onToggle={() => setGeneralOpen(!generalOpen)}
            >
              <div className="space-y-1">
                {/* Status — styled dropdown */}
                <div className="flex items-center justify-between border-b border-border/50 py-2">
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: Custom dropdown component */}
                  <label className="text-sm text-muted-foreground">
                    {t("checklist.edit.statusLabel")}
                  </label>
                  <div className="w-40">
                    <StyledSelect
                      value={form.watch("status")}
                      options={statusOptions}
                      onChange={(val) =>
                        form.setValue(
                          "status",
                          val as "MISSING" | "UPLOADED" | "APPROVED", // dropdown value is string; narrowing for form schema
                          { shouldDirty: true },
                        )
                      }
                      hasColorDot
                    />
                  </div>
                </div>

                {/* Required toggle */}
                <div className="flex items-center justify-between border-b border-border/50 py-2">
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: Toggle switch is a custom button-based control */}
                  <label className="text-sm text-muted-foreground">
                    {t("checklist.edit.requiredLabel")}
                  </label>
                  <ToggleSwitch
                    checked={form.watch("required")}
                    onChange={(val) => form.setValue("required", val)}
                  />
                </div>

                {/* Annex toggle */}
                <div className="flex items-center justify-between py-2">
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: Toggle switch is a custom button-based control */}
                  <label className="text-sm text-muted-foreground">
                    {t("checklist.edit.annexLabel")}
                  </label>
                  <ToggleSwitch
                    checked={form.watch("isAnnex")}
                    onChange={(val) => form.setValue("isAnnex", val)}
                  />
                </div>
              </div>
            </CollapsibleSection>

            {/* Notes */}
            <div className="space-y-1">
              <label htmlFor="create-notes" className="text-sm font-medium">
                {t("checklist.edit.notesLabel")}
              </label>
              <Textarea
                id="create-notes"
                {...form.register("notes")}
                rows={2}
                className="w-full"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("common:actions.create")}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
